import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useSelector, useDispatch } from "react-redux";
import { closeModal } from "../../store/modal/modalReducer";
import axios from "axios";
import { toast } from "react-toastify";
import SubmitButton from "../Forms/SubmitButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faTimes } from "@fortawesome/pro-regular-svg-icons";

export default function ModalBankingDetails(props) {
	const dispatch = useDispatch();
	const bookingsState = useSelector((state) => state.bookings);
	const current_booking = bookingsState?.current_booking;
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Close modal if no booking is available
	useEffect(() => {
		if (!current_booking) {
			dispatch(closeModal());
		}
	}, [current_booking, dispatch]);

	const [formData, setFormData] = useState({
		banking_bank: "",
		banking_account_type: "",
		banking_branch: "",
		banking_branch_code: "",
		banking_bank_code: "",
		banking_account_number: "",
		banking_account_holder: "",
	});

	useEffect(() => {
		if (current_booking?.user?.profile) {
			const profile = current_booking.user.profile;
			setFormData({
				banking_bank: profile.banking_bank || "",
				banking_account_type: profile.banking_account_type || "",
				banking_branch: profile.banking_branch || "",
				banking_branch_code: profile.banking_branch_code || "",
				banking_bank_code: profile.banking_bank_code || "",
				banking_account_number: profile.banking_account_number || "",
				banking_account_holder: profile.banking_account_holder || "",
			});
		}
	}, [current_booking]);

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!current_booking?.user?.id) {
			toast.error("User information not available");
			return;
		}
		
		setIsSubmitting(true);

		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_URL}user_banking_details`,
				{
					user_id: current_booking.user.id,
					...formData,
				}
			);

			toast.success("Banking details updated successfully");

			// Refresh the booking data if callback provided
			if (props.onUpdate) {
				props.onUpdate();
			}

			dispatch(closeModal());
		} catch (error) {
			toast.error(error.response?.data?.message || "Failed to update banking details");
		} finally {
			setIsSubmitting(false);
		}
	};

	// Don't render if no booking data
	if (!current_booking) {
		return null;
	}

	return (
		<Transition.Root show={true} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={() => dispatch(closeModal())}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
				</Transition.Child>

				<div className="fixed inset-0 z-10 overflow-y-auto">
					<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
								<div>
									<div className="mt-3 text-center sm:mt-5">
										<Dialog.Title
											as="h3"
											className="text-lg font-semibold leading-6 text-gray-900"
										>
											Update Banking Details
										</Dialog.Title>
										<p className="text-sm text-gray-500 mt-2">
											{current_booking?.user?.name} {current_booking?.user?.surname}
										</p>
									</div>

									<form onSubmit={handleSubmit} className="mt-6">
										<div className="grid grid-cols-2 gap-4">
											<div>
												<label className="block text-sm font-medium text-gray-700">
													Bank Name
												</label>
												<input
													type="text"
													name="banking_bank"
													value={formData.banking_bank}
													onChange={handleChange}
													required
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
												/>
											</div>

											<div>
												<label className="block text-sm font-medium text-gray-700">
													Account Type
												</label>
												<select
													name="banking_account_type"
													value={formData.banking_account_type}
													onChange={handleChange}
													required
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
												>
													<option value="">Select Type</option>
													<option value="Cheque">Cheque</option>
													<option value="Savings">Savings</option>
													<option value="Current">Current</option>
												</select>
											</div>

											<div>
												<label className="block text-sm font-medium text-gray-700">
													Branch Name
												</label>
												<input
													type="text"
													name="banking_branch"
													value={formData.banking_branch}
													onChange={handleChange}
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
												/>
											</div>

											<div>
												<label className="block text-sm font-medium text-gray-700">
													Branch Code
												</label>
												<input
													type="text"
													name="banking_branch_code"
													value={formData.banking_branch_code}
													onChange={handleChange}
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
												/>
											</div>

											<div>
												<label className="block text-sm font-medium text-gray-700">
													Bank Code <span className="text-red-500">*</span>
												</label>
												<input
													type="text"
													name="banking_bank_code"
													value={formData.banking_bank_code}
													onChange={handleChange}
													required
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
												/>
											</div>

											<div>
												<label className="block text-sm font-medium text-gray-700">
													Account Number <span className="text-red-500">*</span>
												</label>
												<input
													type="text"
													name="banking_account_number"
													value={formData.banking_account_number}
													onChange={handleChange}
													required
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
												/>
											</div>

											<div className="col-span-2">
												<label className="block text-sm font-medium text-gray-700">
													Account Holder Name
												</label>
												<input
													type="text"
													name="banking_account_holder"
													value={formData.banking_account_holder}
													onChange={handleChange}
													className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
												/>
											</div>
										</div>

										<div className="mt-6 flex gap-3 justify-end">
											<button
												type="button"
												onClick={() => dispatch(closeModal())}
												disabled={isSubmitting}
												className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
											>
												<FontAwesomeIcon icon={faTimes} className="mr-2 h-4 w-4" />
												Cancel
											</button>
											<button
												type="submit"
												disabled={isSubmitting}
												className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
											>
												<FontAwesomeIcon icon={faSave} className="mr-2 h-4 w-4" />
												{isSubmitting ? "Saving..." : "Save Banking Details"}
											</button>
										</div>
									</form>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition.Root>
	);
}
